export interface TradingData {
  tradeId: number;
  type: string;
  signal: string;
  datetime: Date;
  price: number;
  contracts: number;
  profit: number;
  profitPercent: number;
  cumulativeProfit: number;
  cumulativeProfitPercent: number;
  total: number
  // totalPercent: number;
  // loss: number;
  // lossPercent: number;
  customId?: string;
}

export interface TradingViewConfigData {
  tradingRange: string;
  backtestingRange: string;
  symbol: string;
  timeframe: string;
  chartType: string;
  pointValue: string;
  currency: string;
  tickSize: string;
  precision: string;
  runOptimization: string;
  placeLimitOrdersImmediatelyAtEntry: string;
  doNotTradeVeryHighVolatility: string;
  doNotTradeHighVolatility: string;
  doNotTradeAverageVolatility: string;
  doNotTradeLowVolatility: string;
  optimizationAIAggressiveness: string;
  aiDirection: string;
  minimumATRTP_SL: string;
  bestLongStrategyNumber: string;
  bestShortStrategyNumber: string;
  initialCapital: string;
  orderSize: string;
  pyramiding: string;
  commission: string;
  slippage: string;
  verifyPriceForLimitOrders: string;
  marginForLongPositions: string;
  marginForShortPositions: string;
  recalculateAfterOrderIsFilled: string;
  recalculateOnEveryTick: string;
  recalculateOnBarClose: string;
  backtestingPrecisionUseBarMagnifier: string;
}

export interface ParsedConfigData {
  indicator: { [key: string]: string };
  tradingView: TradingViewConfigData;
}

export class CSVParser {
  static parse(csv: string): TradingData[] {
    const lines = csv.split("\n").filter(line => line.trim() !== "");
    if (lines.length < 2) {
      throw new Error("CSV must have at least a header row and one data row.");
    }

    // Parse header row
    const rawHeaders = lines[0].split(",").map(header => header.trim());

    // Parse data rows
    const dataRows = lines.slice(1);
    const result: TradingData[] = [];

    for (const row of dataRows) {
      const values = row.split(",").map(value => value.trim());

      
      // Skip rows with missing or empty columns
      if (values.length !== rawHeaders.length || values.some(value => value === "")) {
        continue;
      }
      
      const tradeId = parseInt(values[0], 10);
      const datetime = new Date(values[3]);
      const price = Number(values[4]);
      const contracts = Number(values[5]);
      const profit = Number(values[6]);
      const profitPercent = Number(values[7]);
      const cumulativeProfit = Number(values[8]);
      const cumulativeProfitPercent = Number(values[9]);
      
      // Check if fields are correctly represented
      if (isNaN(tradeId) || isNaN(datetime.getTime()) || isNaN(price) || isNaN(contracts) || isNaN(profit) || isNaN(profitPercent) || isNaN(cumulativeProfit) || isNaN(cumulativeProfitPercent)) {
        throw new Error(`Invalid data format in row: ${row}`);
      }

      const rowData: TradingData = {
        tradeId,
        type: values[1],
        signal: values[2],
        datetime,
        price,
        contracts,
        profit,
        profitPercent,
        cumulativeProfit,
        cumulativeProfitPercent,
        total: 0, // will be completly recalculated
        // totalPercent: Number(values[11]),
        // loss: Number(values[12]),
        // lossPercent: Number(values[13])
      };

      result.push(rowData);
    }

    // sort by date
    result.sort((a, b) => a.datetime.getTime() - b.datetime.getTime());
    return result;
  }

  static parseConfig(csv: string): ParsedConfigData {
    const lines = csv.split("\n").filter(line => line.trim() !== "");
    if (lines.length < 2) {
      throw new Error("CSV must have at least a header row and one data row.");
    }

    // const knownKeys = [
    //   "Nova", "Impulse", "..."
    // ];

    const knownConfig: Partial<TradingViewConfigData> = {};
    const dynamicConfig: { [key: string]: string } = {};

    for (let i=1; i < lines.length; i++) {
      const row = lines[i];
      const [title, value] = row.split(",").map(value => value.trim());
      switch (i) {
        case 1:
          knownConfig.tradingRange = value;
          break;
        case 2:
          knownConfig.backtestingRange = value;
          break;
        case 3:
          knownConfig.symbol = value;
          break;
        case 4:
          knownConfig.timeframe = value;
          break;
        case 5:
          knownConfig.chartType = value;
          break;
        case 6:
          knownConfig.pointValue = value;
          break;
        case 7:
          knownConfig.currency = value;
          break;
        case 8:
          knownConfig.tickSize = value;
          break;
        case 9:
          knownConfig.precision = value;
          break;
        case lines.length - 12:
          knownConfig.initialCapital = value;
          break;
        case lines.length - 11:
          knownConfig.orderSize = value;
          break;
        case lines.length - 10:
          knownConfig.pyramiding = value;
          break;
        case lines.length - 9:
          knownConfig.commission = value;
          break;
        case lines.length - 8:
          knownConfig.slippage = value;
          break;
        case lines.length - 7:
          knownConfig.verifyPriceForLimitOrders = value;
          break;
        case lines.length - 6:
          knownConfig.marginForLongPositions = value;
          break;
        case lines.length - 5:
          knownConfig.marginForShortPositions = value;
          break;
        case lines.length - 4:
          knownConfig.recalculateAfterOrderIsFilled = value;
          break;
        case lines.length - 3:
          knownConfig.recalculateOnEveryTick = value;
          break;
        case lines.length - 2:
          knownConfig.recalculateOnBarClose = value;
          break;
        case lines.length - 1:
          knownConfig.backtestingPrecisionUseBarMagnifier = value;
          break;
        default:
          dynamicConfig[title] = value;
          break;
        }
      }

    return {
      tradingView: knownConfig as TradingViewConfigData,
      indicator: dynamicConfig
    };
  }
}
